/**
 * Training-feature API.
 *
 * Per the 2026-05-04 Phase-B graduation decision (Option A from the
 * gap-analysis discussion): lesson content is a static catalogue
 * bundled with the app (`@/data/training-lessons`); per-device progress
 * lives in `localStorage`. No network round-trips, no backend
 * dependency. The original Replit mock-api implementation backed these
 * functions with `fetch` calls against `/api/training/*`; that mock-api
 * is being discarded (RB-H1) and the Ktor backend has no training
 * endpoints yet, so this module ships entirely client-side.
 *
 * The four exported functions keep their original async signatures so
 * consuming pages (TrainingPage, LessonPage) don't need refactoring;
 * each just resolves immediately. When a Ktor training endpoint
 * exists, swap this module's implementations to call it — the public
 * shape is stable.
 */
import { LESSONS } from "@/data/training-lessons";

// ── Types ────────────────────────────────────────────────────────────────────

export type LessonCategory =
  | "signs"
  | "speed"
  | "right-of-way"
  | "road-rules"
  | "alcohol-drugs"
  | "pedestrians"
  | "parking"
  | "weather";

export interface Lesson {
  id: string;
  title: string;
  category: LessonCategory;
  duration_min: number;
  emoji: string;
  colorFrom: string;
  colorTo: string;
  summary: string;
  tags: string[];
  completed: boolean;
}

export type LessonBlock =
  | { type: "heading"; text: string }
  | { type: "text"; body: string }
  | { type: "tip"; title?: string; body: string }
  | { type: "warning"; title?: string; body: string }
  | { type: "steps"; title?: string; items: string[] }
  | {
      type: "comparison";
      left_label: string;
      left: string;
      right_label: string;
      right: string;
    }
  | { type: "callout"; emoji: string; title: string; body: string }
  | { type: "image"; src: string; alt: string; caption?: string };

export interface CountryDiff {
  country_code: string;
  country_name: string;
  flag: string;
  summary: string;
  severity: "major" | "moderate" | "minor";
  details: string;
}

export interface LessonDetail extends Lesson {
  content: LessonBlock[];
  country_diffs: CountryDiff[];
  related_lesson_ids: string[];
}

/**
 * Lesson content as stored in the static catalogue — same shape as
 * `LessonDetail` minus the per-user `completed` flag, which is composed
 * in at runtime from `localStorage`.
 */
export type StaticLessonContent = Omit<LessonDetail, "completed">;

export interface LessonProgress {
  lesson_id: string;
  completed: boolean;
  completed_at_ms: number | null;
}

export const CATEGORY_META: Record<
  LessonCategory,
  { label: string; emoji: string }
> = {
  signs: { label: "Signs & Signals", emoji: "🚦" },
  speed: { label: "Speed & Zones", emoji: "🏎️" },
  "right-of-way": { label: "Right of Way", emoji: "🛑" },
  "road-rules": { label: "Road Rules", emoji: "🛣️" },
  "alcohol-drugs": { label: "Alcohol & Drugs", emoji: "🍺" },
  pedestrians: { label: "Pedestrians", emoji: "🚶" },
  parking: { label: "Parking", emoji: "🅿️" },
  weather: { label: "Weather & Night", emoji: "🌧️" },
};

// ── Progress (localStorage) ──────────────────────────────────────────────────
//
// Storage shape: `{ "<lesson-id>": { completed, completed_at_ms } }`.
// Versioned key so a future schema change can be a clean reset (we never
// store identifying data here, so reset on schema change is acceptable).

const STORAGE_KEY = "rb:training-progress:v1";

type ProgressEntry = { completed: boolean; completed_at_ms: number | null };
type ProgressMap = Record<string, ProgressEntry>;

function loadProgress(): ProgressMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ProgressMap) : {};
  } catch {
    return {};
  }
}

function saveProgress(progress: ProgressMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // Quota exceeded or storage unavailable — silently ignore. Progress
    // is convenience, not data we can't recompute by re-reading lessons.
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function getLessons(params?: {
  category?: LessonCategory;
  state?: string;
}): Promise<{ lessons: Lesson[] }> {
  const progress = loadProgress();
  const summaries: Lesson[] = LESSONS.map((l) => ({
    id: l.id,
    title: l.title,
    category: l.category,
    duration_min: l.duration_min,
    emoji: l.emoji,
    colorFrom: l.colorFrom,
    colorTo: l.colorTo,
    summary: l.summary,
    tags: [...l.tags],
    completed: progress[l.id]?.completed ?? false,
  }));
  const filtered = params?.category
    ? summaries.filter((l) => l.category === params.category)
    : summaries;
  return { lessons: filtered };
}

export async function getLesson(id: string): Promise<LessonDetail> {
  const lesson = LESSONS.find((l) => l.id === id);
  if (!lesson) throw new Error(`Lesson not found: ${id}`);
  const progress = loadProgress();
  return {
    ...lesson,
    tags: [...lesson.tags],
    content: [...lesson.content],
    country_diffs: [...lesson.country_diffs],
    related_lesson_ids: [...lesson.related_lesson_ids],
    completed: progress[id]?.completed ?? false,
  };
}

export async function markLessonComplete(
  id: string,
  completed: boolean,
): Promise<void> {
  const progress = loadProgress();
  progress[id] = {
    completed,
    completed_at_ms: completed ? Date.now() : null,
  };
  saveProgress(progress);
}

export async function getTrainingProgress(): Promise<{
  progress: LessonProgress[];
  completedCount: number;
  totalCount: number;
}> {
  const progress = loadProgress();
  const lessonProgress: LessonProgress[] = LESSONS.map((l) => ({
    lesson_id: l.id,
    completed: progress[l.id]?.completed ?? false,
    completed_at_ms: progress[l.id]?.completed_at_ms ?? null,
  }));
  return {
    progress: lessonProgress,
    completedCount: lessonProgress.filter((p) => p.completed).length,
    totalCount: LESSONS.length,
  };
}
